const PROGRESS_STORAGE_PREFIX = 'watchvim:v3:progress:v1';
const PROGRESS_EVENT = 'watchvim:v3:progress-changed';
const COMPLETE_RATIO = 0.95;
const COMPLETE_REMAINING_SECONDS = 20;

function firstString(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function rawFor(item) {
  return item?.raw && typeof item.raw === 'object' ? item.raw : (item || {});
}

export function contentPlaybackId(item) {
  const raw = rawFor(item);
  return firstString(
    item?.playbackId,
    item?.contentPlaybackId,
    raw?.contentPlaybackId,
    raw?.content_playback_id,
    raw?.contentPlaybackID,
    raw?.playbackId,
    raw?.playback_id,
    raw?.muxPlaybackId,
    raw?.mux_playback_id,
    raw?.playback?.id,
    raw?.playback?.playbackId,
  );
}

export function trailerPlaybackId(item) {
  const raw = rawFor(item);
  return firstString(
    item?.trailerPlaybackId,
    raw?.trailerPlaybackId,
    raw?.trailer_playback_id,
    raw?.trailerPlaybackID,
    raw?.trailer?.playbackId,
    raw?.trailer?.playback_id,
  );
}

export function playbackIdFor(item, kind = 'content') {
  return String(kind || 'content').toLowerCase() === 'trailer'
    ? trailerPlaybackId(item)
    : contentPlaybackId(item);
}

export function titlePlaybackKey(item, kind = 'content') {
  const id = String(item?.id || item?.slug || '').trim();
  return id ? `title:${id}:${String(kind || 'content').toLowerCase()}` : '';
}

export function episodePlaybackKey(series, episode) {
  const seriesId = String(series?.id || series?.slug || episode?.seriesId || '').trim();
  const season = Number(episode?.season) || 1;
  const number = Number(episode?.episode) || 1;
  return seriesId ? `episode:${seriesId}:s${season}e${number}` : '';
}

export function viewerProgressScope(user) {
  const userId = String(user?.id || '').trim();
  return userId ? `user:${userId}` : 'guest';
}

function storageKey(viewerScope) {
  return `${PROGRESS_STORAGE_PREFIX}:${encodeURIComponent(String(viewerScope || 'guest'))}`;
}

function readStore(viewerScope) {
  if (typeof window === 'undefined' || !window.localStorage) return {};
  try {
    const raw = window.localStorage.getItem(storageKey(viewerScope));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
}

function notifyProgressChanged(viewerScope, mediaKey) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  try {
    window.dispatchEvent(new CustomEvent(PROGRESS_EVENT, { detail: { viewerScope, mediaKey } }));
  } catch (_) {}
}

function writeStore(viewerScope, value, mediaKey = '') {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    window.localStorage.setItem(storageKey(viewerScope), JSON.stringify(value || {}));
    notifyProgressChanged(viewerScope, mediaKey);
    return true;
  } catch (_) {
    return false;
  }
}

export function playbackProgressEventName() {
  return PROGRESS_EVENT;
}

export function listPlaybackProgress(viewerScope) {
  return Object.entries(readStore(viewerScope))
    .map(([mediaKey, record]) => {
      const seconds = Number(record?.seconds);
      const duration = Number(record?.duration);
      if (!mediaKey || !Number.isFinite(seconds) || seconds <= 0) return null;
      return {
        mediaKey,
        seconds,
        duration: Number.isFinite(duration) && duration > 0 ? duration : 0,
        updatedAt: Number(record?.updatedAt) || 0,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export function readPlaybackProgress(viewerScope, mediaKey) {
  if (!mediaKey) return null;
  return listPlaybackProgress(viewerScope).find((record) => record.mediaKey === mediaKey) || null;
}

export function mergePlaybackProgress(viewerScope, records = []) {
  const store = readStore(viewerScope);
  let changed = false;
  let lastKey = '';
  for (const record of Array.isArray(records) ? records : []) {
    const mediaKey = String(record?.mediaKey || '').trim();
    const seconds = Math.max(0, Number(record?.seconds) || 0);
    const duration = Math.max(0, Number(record?.duration) || 0);
    const updatedAt = Number(record?.updatedAt) || 0;
    if (!mediaKey || seconds < 1) continue;
    const existingAt = Number(store[mediaKey]?.updatedAt) || 0;
    if (existingAt > updatedAt) continue;
    store[mediaKey] = {
      seconds: Math.floor(seconds),
      duration: duration > 0 ? Math.floor(duration) : 0,
      updatedAt: updatedAt || Date.now(),
    };
    changed = true;
    lastKey = mediaKey;
  }
  return changed ? writeStore(viewerScope, store, lastKey) : true;
}

export function clearPlaybackProgress(viewerScope, mediaKey) {
  if (!mediaKey) return false;
  const store = readStore(viewerScope);
  if (!(mediaKey in store)) return true;
  delete store[mediaKey];
  return writeStore(viewerScope, store, mediaKey);
}

export function savePlaybackProgress(viewerScope, mediaKey, seconds, duration = 0) {
  if (!mediaKey) return false;
  const current = Math.max(0, Number(seconds) || 0);
  const total = Math.max(0, Number(duration) || 0);

  if (total > 0) {
    const ratio = current / total;
    const remaining = total - current;
    if (ratio >= COMPLETE_RATIO || remaining <= COMPLETE_REMAINING_SECONDS) {
      return clearPlaybackProgress(viewerScope, mediaKey);
    }
  }

  if (current < 1) return true;

  const store = readStore(viewerScope);
  store[mediaKey] = {
    seconds: Math.floor(current),
    duration: total > 0 ? Math.floor(total) : 0,
    updatedAt: Date.now(),
  };
  return writeStore(viewerScope, store, mediaKey);
}

export function resolveTitlePlayback(item, kind = 'content') {
  const normalizedKind = String(kind || 'content').toLowerCase() === 'trailer' ? 'trailer' : 'content';
  return {
    kind: normalizedKind,
    playbackId: playbackIdFor(item, normalizedKind),
    mediaKey: titlePlaybackKey(item, normalizedKind),
    titleId: String(item?.id || item?.slug || ''),
    parentTitleId: '',
    title: String(item?.title || ''),
    poster: item?.backdrop || item?.image || '',
  };
}

export function resolveEpisodePlayback(series, episode) {
  return {
    kind: 'episode',
    playbackId: contentPlaybackId(episode),
    mediaKey: episodePlaybackKey(series, episode),
    titleId: String(episode?.id || ''),
    parentTitleId: String(series?.id || series?.slug || ''),
    title: String(episode?.title || series?.title || ''),
    seriesTitle: String(series?.title || ''),
    poster: episode?.image || series?.backdrop || series?.image || '',
  };
}
